import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI, AdminGroupSummary, AdminUser, DashboardRole, TrainerUser } from '../api';
import { useAuth } from '../contexts/AuthContext';
import { formatDateTime } from '../utils/dateUtils';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { logout, user: currentUser } = useAuth();

  const [users, setUsers] = useState<AdminUser[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<AdminUser[]>([]);
  const [trainers, setTrainers] = useState<TrainerUser[]>([]);
  const [groups, setGroups] = useState<AdminGroupSummary[]>([]);
  const [viewerRole, setViewerRole] = useState<DashboardRole | null>(currentUser?.role || null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1 });
  const [updatingUserId, setUpdatingUserId] = useState<number | null>(null);

  const isSuperAdmin = viewerRole === 'super_admin';

  const hasMissingTargets = (u: AdminUser) => {
    return u.target_carbs == null || u.target_protein == null || u.target_fat == null;
  };

  const missingTargetsCount = useMemo(
    () => users.filter(hasMissingTargets).length,
    [users]
  );

  useEffect(() => {
    if (searchTerm) {
      const filtered = users.filter((u) =>
        u.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredUsers(filtered);
    } else {
      setFilteredUsers(users);
    }
  }, [searchTerm, users]);

  const loadTrainers = useCallback(async () => {
    const trainerResponse = await adminAPI.getTrainers();
    if (trainerResponse.success) {
      setTrainers(trainerResponse.trainers);
    } else {
      setError(trainerResponse.error || 'Failed to load trainers');
    }
  }, []);

  const loadGroups = useCallback(async () => {
    const groupResponse = await adminAPI.getGroups();
    if (groupResponse.success) {
      setGroups(groupResponse.groups);
    } else {
      setError(groupResponse.error || 'Failed to load groups');
    }
  }, []);

  const loadUsers = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const response = await adminAPI.getUsers(page, 50);
      if (response.success) {
        setViewerRole(response.viewerRole);
        setUsers(response.users);
        setFilteredUsers(response.users);
        setPagination({
          total: response.pagination.total,
          totalPages: response.pagination.totalPages,
        });

        if (response.viewerRole === 'super_admin') {
          await Promise.all([loadTrainers(), loadGroups()]);
        } else {
          setTrainers([]);
          setGroups([]);
        }
      } else {
        setError(response.error || 'Failed to load users');
      }
    } catch (err: any) {
      if (err.response?.status === 403) {
        setError('Dashboard access required. Your account must be super admin or trainer.');
      } else {
        setError('Failed to load users');
      }
    } finally {
      setIsLoading(false);
    }
  }, [loadGroups, loadTrainers, page]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleTrainerStatusToggle = async (targetUser: AdminUser, makeTrainer: boolean) => {
    setUpdatingUserId(targetUser.id);
    setError('');
    try {
      const response = await adminAPI.setUserTrainerStatus(targetUser.id, makeTrainer);
      if (!response.success) {
        setError(response.error || 'Failed to update trainer status');
      } else {
        await loadUsers();
      }
    } catch {
      setError('Failed to update trainer status');
    } finally {
      setUpdatingUserId(null);
    }
  };

  const handleTrainerAssignmentChange = async (targetUser: AdminUser, value: string) => {
    const trainerUserId = value ? parseInt(value, 10) : null;
    setUpdatingUserId(targetUser.id);
    setError('');
    try {
      const response = await adminAPI.assignTrainerToClient(targetUser.id, trainerUserId);
      if (!response.success) {
        setError(response.error || 'Failed to assign trainer');
      } else {
        await loadUsers();
      }
    } catch {
      setError('Failed to assign trainer');
    } finally {
      setUpdatingUserId(null);
    }
  };

  const dashboardTitle = viewerRole === 'super_admin'
    ? 'Admin Dashboard'
    : viewerRole === 'trainer'
      ? 'Trainer Dashboard'
      : 'Dashboard';
  const searchPlaceholder = isSuperAdmin ? 'Search by email...' : 'Search clients by email...';

  return (
    <div className="min-h-screen bg-gray-100">
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{dashboardTitle}</h1>
            <p className="text-xs text-gray-500 mt-1">
              {isSuperAdmin ? 'Super admin view' : 'Client management view'}
            </p>
          </div>
          <div className="flex items-center space-x-4">
            <span className="text-sm text-gray-600">{currentUser?.email}</span>
            <button
              onClick={logout}
              className="text-sm text-red-600 hover:text-red-800"
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6 bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-8">
              <div>
                <p className="text-sm text-gray-500">{isSuperAdmin ? 'Total Users' : 'Total Clients'}</p>
                <p className="text-3xl font-bold text-gray-900">{pagination.total}</p>
              </div>
              {missingTargetsCount > 0 && (
                <div>
                  <p className="text-sm text-amber-600">Missing Targets</p>
                  <p className="text-3xl font-bold text-amber-600">{missingTargetsCount}</p>
                </div>
              )}
            </div>
            {isSuperAdmin && (
              <button
                onClick={() => navigate('/active-users')}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
              >
                View Active Users
              </button>
            )}
          </div>
        </div>

        {isSuperAdmin && (
          <div className="mb-6 bg-white rounded-lg shadow overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between gap-4 flex-wrap">
              <div>
                <h2 className="text-lg font-medium text-gray-900">Groups</h2>
                <p className="text-sm text-gray-500">Shared across super admins. Create cohorts and monitor their user activity together.</p>
              </div>
              <button
                onClick={() => navigate('/groups/new')}
                className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 text-sm"
              >
                Create Group
              </button>
            </div>

            {isLoading ? (
              <div className="p-6 text-sm text-gray-500">Loading groups...</div>
            ) : groups.length === 0 ? (
              <div className="p-6 text-sm text-gray-500">
                No groups yet. Create one to organize users and review their activity together.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Group
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Members
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Updated
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {groups.map((group) => (
                      <tr
                        key={group.id}
                        className="hover:bg-gray-50 cursor-pointer"
                        onClick={() => navigate(`/groups/${group.id}`)}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{group.name}</div>
                          <div className="text-xs text-gray-500">Created {formatDateTime(group.created_at)}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {group.member_count}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {formatDateTime(group.updated_at)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button
                            onClick={(event) => {
                              event.stopPropagation();
                              navigate(`/groups/${group.id}`);
                            }}
                            className="text-blue-600 hover:text-blue-900 mr-4"
                          >
                            View
                          </button>
                          <button
                            onClick={(event) => {
                              event.stopPropagation();
                              navigate(`/groups/${group.id}/edit`);
                            }}
                            className="text-gray-700 hover:text-gray-900"
                          >
                            Edit
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        <div className="mb-6">
          <input
            type="text"
            placeholder={searchPlaceholder}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full max-w-md px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        <div className="bg-white shadow rounded-lg overflow-hidden">
          {isLoading ? (
            <div className="p-8 flex justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    {isSuperAdmin && (
                      <>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Role
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Assigned Trainer
                        </th>
                      </>
                    )}
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Last Login
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredUsers.map((rowUser) => {
                    const rowIsTrainer = Boolean(rowUser.is_trainer);
                    const rowIsSuperAdmin = Boolean(rowUser.is_super_admin);
                    const disableTrainerAssignment = rowIsSuperAdmin || updatingUserId === rowUser.id;

                    return (
                      <tr
                        key={rowUser.id}
                        className="hover:bg-gray-50 cursor-pointer"
                        onClick={() => navigate(`/users/${rowUser.id}`)}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <div className="text-sm font-medium text-gray-900">{rowUser.email}</div>
                            {hasMissingTargets(rowUser) && (
                              <span className="px-1.5 py-0.5 text-xs bg-amber-100 text-amber-700 rounded" title="Missing macro targets">
                                No targets
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-gray-500">ID: {rowUser.id}</div>
                        </td>

                        {isSuperAdmin && (
                          <>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                              {rowIsSuperAdmin ? (
                                <span className="px-2 py-1 rounded bg-gray-900 text-white text-xs">Super Admin</span>
                              ) : (
                                <label
                                  className="inline-flex items-center gap-2"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <input
                                    type="checkbox"
                                    checked={rowIsTrainer}
                                    disabled={updatingUserId === rowUser.id}
                                    onChange={(e) => handleTrainerStatusToggle(rowUser, e.target.checked)}
                                  />
                                  <span>Trainer</span>
                                </label>
                              )}
                            </td>
                            <td
                              className="px-6 py-4 whitespace-nowrap text-sm text-gray-700"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {rowIsSuperAdmin ? (
                                <span className="text-xs text-gray-400">N/A</span>
                              ) : (
                                <select
                                  value={rowUser.trainer_user_id ?? ''}
                                  onChange={(e) => handleTrainerAssignmentChange(rowUser, e.target.value)}
                                  disabled={disableTrainerAssignment}
                                  className="px-2 py-1 border border-gray-300 rounded text-sm bg-white disabled:bg-gray-100"
                                >
                                  <option value="">No trainer</option>
                                  {trainers
                                    .filter((trainer) => trainer.id !== rowUser.id)
                                    .map((trainer) => (
                                      <option key={trainer.id} value={trainer.id}>
                                        {trainer.email}
                                      </option>
                                    ))}
                                </select>
                              )}
                            </td>
                          </>
                        )}

                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDateTime(rowUser.created_at)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDateTime(rowUser.last_login)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/users/${rowUser.id}`);
                            }}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            View Details
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {pagination.totalPages > 1 && (
          <div className="mt-4 flex justify-center space-x-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Previous
            </button>
            <span className="px-4 py-2 text-sm text-gray-700">
              Page {page} of {pagination.totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
              disabled={page === pagination.totalPages}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Next
            </button>
          </div>
        )}
      </main>
    </div>
  );
};

export default Dashboard;
